/**
 * useSessionTimeout Hook
 * Implementeert automatische logout na 30 minuten inactiviteit.
 */

import { useEffect, useRef, useState } from 'react'
import { useAuthStore } from '../stores/authStore'
import { useNavigate } from 'react-router-dom'

const SESSION_TIMEOUT_MS = 30 * 60 * 1000 // 30 minuten
const WARNING_TIME_MS = 5 * 60 * 1000 // Waarschuwing 5 minuten voor timeout

export function useSessionTimeout() {
  const logout = useAuthStore((state) => state.logout)
  const navigate = useNavigate()
  const timeoutRef = useRef(null)
  const warningTimeoutRef = useRef(null)
  const lastActivityRef = useRef(Date.now())
  const [showExpiredModal, setShowExpiredModal] = useState(false)
  
  const resetTimeout = () => {
    lastActivityRef.current = Date.now()
    
    // Clear existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current)
    }
    
    // Set warning timeout (5 minuten voor einde)
    warningTimeoutRef.current = setTimeout(() => {
      // Optioneel: toon waarschuwing (kan later worden toegevoegd)
    }, SESSION_TIMEOUT_MS - WARNING_TIME_MS)
    
    // Set main timeout
    timeoutRef.current = setTimeout(() => {
      setShowExpiredModal(true)
    }, SESSION_TIMEOUT_MS)
  }
  
  const handleLogout = () => {
    setShowExpiredModal(false)
    logout()
    navigate('/login', { 
      state: { 
        message: 'Uw sessie is verlopen door inactiviteit. Log opnieuw in om door te gaan.' 
      } 
    })
  }
  
  useEffect(() => {
    // Reset timeout bij user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'keydown']
    
    const handleActivity = () => {
      resetTimeout()
    }
    
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true)
    })
    
    // Start initial timeout
    resetTimeout()
    
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true)
      })
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current)
      }
    }
  }, [logout, navigate])
  
  return {
    resetTimeout,
    showExpiredModal,
    handleLogout
  }
}
